// ONE AVATAR, drawn wherever this product draws a person.
//
// # What was there instead
//
// Three implementations of "two letters from a name", each written where it was needed
// and each pointing at the next one in a comment:
//
//   `ui/account.tsx`     split a display string, first letter of two words
//   `ui/person.tsx`      `family[0] + given[0]`
//   `app/org-graph.tsx`  `given[0] + family[0]`   <- the other way round
//
// The third disagreed with the other two and had done since it was written. Nothing was
// red, because two letters in either order look like two letters: the chart said `EK`
// where the roster and the hover card said `KE` about the same person, and the only way
// to notice is to have both on screen at once. That is the ordinary cost of a rule
// implemented at each call site, and `personName` — family first, "so a person is written
// the same way wherever they appear" — had already settled the question the chart was
// answering differently.
//
// Four more sites drew the circle itself, each with its own copy of the same six classes.
//
// # THE PHOTOGRAPH GOES THROUGH HERE OR IT GOES NOWHERE
//
// `GET /v1/people/{person_id}/photograph` exists now, so a person riksu holds a picture of
// can be drawn as one. That is exactly the sort of thing that arrives at one call site and
// never reaches the other four, so it is a PROP on this component and there is no other
// way to render one.
//
// # WHERE A PHOTOGRAPH DOES NOT GO, and why that is not a gap
//
// Every list. `GET /v1/people` carries no photograph, and putting one on it would be two
// separate defects: a read per row, which is the ninety-requests-to-draw-one-screen
// mistake, and a live read credential for every colleague's face handed to every manager
// on every page load, whether or not anybody looked. A directory photograph visible across
// the tenant is a different decision from one visible to a manager, and it is the product
// owner's rather than this component's. So the chart nodes, the table cells and the hover
// cards draw INITIALS, permanently and correctly, and only surfaces about ONE person ask.

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { ApiError, call, type components } from "../lib/api";
import { useT } from "../shell/i18n";

type PersonPhotograph = components["schemas"]["PersonPhotograph"];

/**
 * Two letters, upper case, from the first two words. Never one, never three.
 *
 * # `tr-TR` IS HARD-CODED HERE ON PURPOSE, and it must not become `useLocale()`
 *
 * This is the opposite rule from collation, and the two are easy to confuse because they
 * are both "locale-sensitive string operations":
 *
 * - **Collation belongs to the READER.** Which order `İpek` and `Irmak` come in is a
 *   question about the alphabet the person scanning the list has in their head, so
 *   `column-kind.tsx` and `picker.tsx` pass `useLocale()`.
 * - **Casing belongs to the DATA.** `"i".toLocaleUpperCase("tr")` is `İ` and
 *   `"i".toLocaleUpperCase("en")` is `I` — measured — and which of those is the right
 *   capital for a person named `irem` is a fact about her name, not about who is reading
 *   it. Reading `İREM`'s initial as `I` because the reader switched to English would
 *   print the wrong letter for a Turkish colleague on a Turkish colleague's screen.
 *
 * Riksu holds no per-person language tag, so the language of the data cannot be resolved
 * from the data. `tr-TR` is the honest constant: this is a Turkish HR product and the
 * names in it are overwhelmingly Turkish. It is reachable — this reads `charAt(0)` of
 * whatever somebody typed, so a lower-case first letter is an ordinary input.
 *
 * Not to be confused with `toUpperCase()` on a currency code (`advances.tsx`,
 * `expense.tsx`), which is locale-INDEPENDENT and must stay that way: `ils` under a
 * Turkish casing would become `İLS` and no such currency exists.
 *
 * # It takes the WRITTEN NAME, never the two halves
 *
 * `personName` is how a person is written in this product — family first — and every
 * caller already has it or can build it. Taking a `{ given, family }` pair here would let
 * the third call site put them in its own order again, which is the defect this file
 * exists to close.
 */
export function initialsOf(name: string): string {
  const words = name
    .trim()
    .split(/[\s._@,-]+/)
    .filter(Boolean);
  const first = words[0]?.[0] ?? "?";
  const second = words[1]?.[0] ?? words[0]?.[1] ?? "";
  return (first + second).toLocaleUpperCase("tr-TR");
}

/**
 * How big, by role rather than by pixels.
 *
 * The four round sizes are the ones already in the product, kept to the pixel so this
 * change moves no layout: `xs` is the inline chip beside a name, `sm` a chart node's
 * list row, `md` the topbar control, `lg` a card header. `portrait` is the odd one and is
 * not a circle at all — see {@link PersonPortrait}.
 */
export type AvatarSize = "xs" | "sm" | "md" | "lg";

const CIRCLE: Record<AvatarSize, string> = {
  xs: "size-5 text-shell-caption",
  sm: "size-6 text-shell-caption",
  md: "size-7 text-shell-caption",
  lg: "size-9 text-shell-label",
};

export interface PersonAvatarProps {
  /** How the person is written — `personName(…)`, never a UUID. */
  readonly name: string;
  /**
   * A time-limited URL riksu handed out for their photograph.
   *
   * **Absent is the ordinary answer and is not a placeholder state.** Initials are the
   * design here, not a frame waiting to be filled: a person riksu holds no picture of is
   * drawn with their letters, permanently, and nothing on the circle suggests otherwise.
   */
  readonly photograph?: string | null;
  readonly size?: AvatarSize;
  readonly className?: string;
}

type ImageArrival = "waiting" | "loaded" | "failed";

/**
 * A person, drawn small: their photograph if riksu could hand one over, else their
 * letters.
 *
 * `aria-hidden` throughout, and that is deliberate rather than an oversight. Every call
 * site puts the person's NAME immediately beside this — a table cell, a chart node, a
 * card header, a menu item — so announcing it would say the name twice, once spelled out
 * letter by letter. The one surface where the name is not beside it is the topbar
 * control, and that button carries its own accessible name.
 */
export function PersonAvatar({ name, photograph, size = "lg", className }: PersonAvatarProps) {
  // A NEW CREDENTIAL IS A NEW CHANCE, adjusted during RENDER rather than in an effect.
  //
  // React's own answer for "reset state when a prop changes", and the pattern this product
  // already uses for the rail's opened area: an effect would paint one frame of the previous
  // person's fallback over the new one, and biome is right that a dependency the body never
  // reads is not a dependency. Comparing against what was last reconciled is what makes it a
  // reset rather than a comparison that keeps matching.
  const [arrival, setArrival] = useState<ImageArrival>(photograph ? "waiting" : "failed");
  const [reconciled, setReconciled] = useState(photograph);
  if (photograph !== reconciled) {
    setReconciled(photograph);
    setArrival(photograph ? "waiting" : "failed");
  }
  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-surface-selected font-medium text-muted-foreground",
        CIRCLE[size],
        className,
      )}
    >
      {/* Initials are the loading surface as well as the permanent no-photo fallback. The
          image covers them only after its bytes decode, so a slow credential never paints
          an empty circle or a broken-image glyph. */}
      <span>{initialsOf(name)}</span>
      {photograph && arrival !== "failed" ? (
        // `object-cover`, because a portrait is rarely square and `object-fill` would
        // stretch a face. The alt is empty rather than absent: the span above is already
        // hidden, and an `alt` naming the person would be the name said twice on any
        // browser that ignores `aria-hidden` on an image's ancestor.
        //
        // `onError` FALLS BACK TO THE INITIALS, which were always the right answer here and
        // were simply unreachable once riksu said 200: the browser drew a broken-image glyph
        // in a circle instead. See `PersonPortrait` for the state this belongs to.
        <img
          src={photograph}
          alt=""
          decoding="async"
          className={cn(
            "absolute inset-0 size-full object-cover",
            arrival === "loaded" ? "opacity-100" : "opacity-0",
          )}
          onLoad={() => setArrival("loaded")}
          onError={() => setArrival("failed")}
        />
      ) : null}
    </span>
  );
}

// ------------------------------------------------------------------ the record's own slot

/**
 * What riksu could tell us about one person's photograph.
 *
 * Three states and not two, because **"nobody filed one" and "this deployment cannot
 * serve one" are different facts and only the first is about the person.** Collapsing
 * them is the correct-absence-over-a-stale-server trap: a screen would render *no
 * photograph on file* about somebody who has one, and it would look exactly like the
 * truth.
 */
export type Photograph =
  /** Nobody has asked yet, or there is no person to ask about. */
  | { readonly status: "unasked" }
  | { readonly status: "asking" }
  | { readonly status: "held"; readonly url: string }
  /**
   * Riksu answered 404. Either nobody has filed one, or one is on file whose bytes are
   * not settled — riksu's own sentence says which, and a READER cannot act on the
   * difference either way, so the screen does not split it. An operator reading the
   * response sees it.
   */
  | { readonly status: "none" }
  /**
   * Riksu holds one and could not hand it over: this deployment supplies no object
   * store, or the store did not answer. **Not the same sentence as `none`.**
   */
  | { readonly status: "unreachable" };

/**
 * Asks riksu where one person's photograph is.
 *
 * # ONE PERSON, and the type is what says so
 *
 * It takes a single id and there is deliberately no list variant. A hook that took an
 * array would be used by a table the week after it landed, and a photograph per row is
 * both a request per row and a live read capability per row — see the note at the top of
 * this file. The absence of the plural form is the enforcement.
 *
 * A refusal is an ORDINARY answer and is never surfaced as a failure: `riksu-authz`
 * narrows by relationship, and a 403 here means the reader asked a reasonable question
 * they may not have answered. It lands on `none`, which draws what a person with no
 * photograph draws — telling somebody *you may not see this face* would disclose that
 * there is one.
 */
export function usePhotograph(personId: string | null): Photograph {
  const [held, setHeld] = useState<Photograph>({ status: "unasked" });

  useEffect(() => {
    if (!personId) {
      setHeld({ status: "unasked" });
      return;
    }
    let current = true;
    setHeld({ status: "asking" });
    call<PersonPhotograph>(`/v1/people/${encodeURIComponent(personId)}/photograph`)
      .then((answer) => {
        if (current) {
          setHeld({ status: "held", url: answer.url });
        }
      })
      .catch((why: unknown) => {
        if (!current) {
          return;
        }
        // 503 IS THE ONE THAT MUST NOT READ AS ABSENCE. Everything else — 404 for no
        // photograph, 403 for a person this caller may not read, a network fault — is
        // "there is nothing to draw", which is what initials already say honestly.
        setHeld({
          status: why instanceof ApiError && why.status === 503 ? "unreachable" : "none",
        });
      });
    return () => {
      current = false;
    };
  }, [personId]);

  return held;
}

export interface PersonPortraitProps {
  readonly name: string;
  readonly photograph: Photograph;
  readonly className?: string;
}

/**
 * A person's photograph at the size somebody actually looks at it, in their record.
 *
 * # THE PLACEHOLDER IS A LOADING SURFACE, NOT AN ERROR ICON
 *
 * The person's letters sit on soft abstract shapes until the photograph has actually
 * decoded. The same surface remains when there is no photograph or its bytes cannot be
 * reached, with a sentence distinguishing those two facts. It contains no image glyph,
 * generic silhouette or invented face, so it cannot be confused with either an error
 * badge or the employee's photograph.
 *
 * # ONE SLOT, ONE CODE PATH
 *
 * The real photograph replaces the frame in place. There is no second component and no
 * branch at any call site: `photograph.status` decides, here, once — so the day a
 * deployment supplies an object store this slot fills in with nothing else changing.
 *
 * # THE THREE ABSENCES ARE THREE SENTENCES
 *
 * `asking` says nothing at all rather than flashing an absence that is about to be
 * contradicted; `none` says riksu holds no photograph, which is a fact about the person;
 * `unreachable` says riksu holds one and cannot serve it, which is a fact about this
 * deployment. Rendering the second where the third is true is the whole reason
 * `GET /v1/people/{id}/photograph` answers 503 rather than 404 for it.
 */
/**
 * The room the frame is given, whatever that turns out to be.
 *
 * A record's photograph sits in a `span="tall"` cell, which is a claim to two grid rows —
 * and how tall two rows are is decided by the panels beside it, not by this. Measured on
 * the dev stack at 1440: the frame is capped at 320px and the two panels beside it ran to
 * 444, so the picture sat at the top of its panel with sixty-five pixels of nothing under
 * it, which is what the product owner reported as *"altta boşluk var"*.
 *
 * The cap stays — it is argued where it is written, and an uncapped square is a 430px face
 * taking a whole screen of scroll in the narrow pane — so the slack is CENTRED rather than
 * left at the bottom. A portrait sitting in the middle of its frame reads as a portrait; the
 * same portrait pinned to the top of a frame that stops early reads as a panel that failed
 * to finish.
 *
 * `h-full` is inert where nothing said how tall to be: in the narrow pane the grid is one
 * column, the section is not stretched, its height is its content's, and the frame is sized
 * by `w-full max-w-80` exactly as before.
 */
const SLOT = "flex h-full items-center justify-center";

export function PersonPortrait({ name, photograph, className }: PersonPortraitProps) {
  const t = useT();

  // THE FOURTH STATE BELONGS TO THE BROWSER.
  //
  // `usePhotograph` reports three, and they are all riksu's bookkeeping: nobody filed one,
  // riksu holds one this deployment cannot serve, and here is a credential for the bytes.
  // Whether the BYTES ACTUALLY ARRIVE is a fact only the browser learns, one hop later, and
  // nothing modelled it — so a 200 whose credential then fails rendered as a broken-image
  // glyph rather than as any of the three.
  //
  // Measured on the dev stack, and this is what forced it: the seed filed every özlük
  // dosyası document with a storage key naming a customer's own store (`dms://…`), on the
  // written ground that "this deployment is not wired to one, and a read for the bytes
  // answers 503". A real bucket was wired in afterwards, so `reach` signed a URL for a key
  // that is not in it and is not even a legal object name — riksu answered 200, the store
  // answered 400, and Chrome blocked the reply as a non-image cross-origin response. A
  // hazard declared moot, whose closing mechanism was removed underneath it.
  //
  // THAT PARTICULAR CAUSE IS GONE AND THIS STATE IS NOT. The seed files through
  // `POST /v1/documents/uploads` now and uploads a real PNG, so a seeded stack no longer
  // manufactures it. What has not changed is the thing this models: riksu's `verification`
  // is a claim about what a store reported when it was ASKED, and the browser's fetch is a
  // separate hop that can fail on its own — an expired credential, a bucket whose CORS says
  // no, a network that is not there, an object deleted underneath a `caller_asserted` row
  // riksu never checked. Removing this because one producer of it was fixed would be the
  // same mistake in the other direction.
  //
  // It degrades to `unreachable` and NOT to `none`: riksu says a photograph is on file, and
  // saying nobody filed one would be a lie in the reassuring direction — the reader would
  // go and file a second. The sentence already exists and already says the true thing.
  // Adjusted during RENDER, for `PersonAvatar`'s reason one component up: without it, one
  // failure would suppress the portrait for every later person the pane opens, and an effect
  // would paint a frame of the previous person's fallback first.
  const [arrival, setArrival] = useState<ImageArrival>("waiting");
  const url = photograph.status === "held" ? photograph.url : null;
  const [reconciled, setReconciled] = useState(url);
  if (url !== reconciled) {
    setReconciled(url);
    setArrival("waiting");
  }

  // `aspect-square` rather than a fixed height, so the slot follows the cell it is given —
  // the pane has two widths, one twice the other, and a photograph measured in pixels would
  // be the wrong size in one of them.
  //
  // `max-w-80`, because "fills the cell" is wrong in the other direction: in the narrow pane
  // the grid collapses to one column and an uncapped square would be a 430px face taking a
  // whole screen of scroll. What CENTRES it is `SLOT` above, on both branches.
  const unavailable = photograph.status === "unreachable" || arrival === "failed";
  const because = unavailable
    ? t("photograph.unreachable")
    : photograph.status === "asking" || photograph.status === "held"
      ? null
      : t("photograph.none");

  return (
    <div className={SLOT}>
      <div
        className={cn(
          "relative flex aspect-square w-full max-w-80 flex-col items-center justify-center gap-3 overflow-hidden rounded-lg border border-line-subtle bg-surface-selected p-4 text-center",
          className,
        )}
      >
        {/* Token-backed shapes keep this visibly friendly without inventing a face. */}
        <span
          aria-hidden="true"
          className="absolute -top-8 -left-8 size-40 rounded-full bg-surface-warning-subtle"
        />
        <span
          aria-hidden="true"
          className="absolute -right-10 -bottom-10 size-48 rounded-full bg-surface-positive opacity-60"
        />
        <span
          aria-hidden="true"
          className="relative flex size-24 items-center justify-center rounded-full bg-surface-panel text-shell-heading font-medium text-muted-foreground shadow-(--shadow-panel)"
        >
          {initialsOf(name)}
        </span>
        {because ? (
          <p className="relative m-0 text-shell-caption text-muted-foreground">{because}</p>
        ) : null}
        {photograph.status === "held" && arrival !== "failed" ? (
          <img
            src={photograph.url}
            decoding="async"
            onLoad={() => setArrival("loaded")}
            onError={() => setArrival("failed")}
            // THE NAME, and no wrapper around it. "Photograph of X" spends a word on what the
            // element already is; a screen reader announces an image as an image.
            alt={name}
            className={cn(
              "absolute inset-0 size-full object-cover",
              arrival === "loaded" ? "opacity-100" : "opacity-0",
            )}
          />
        ) : null}
      </div>
    </div>
  );
}
